import { create } from 'zustand';
import { adminCartApi } from '@api/api-config.ts';
import { AdminCartDetailResponse, AdminCartListResponse } from '@api/generated/portal-be-admin';

export interface CartFilters {
  q?: string;
  organizationId?: number;
  inactiveDays?: number;
  page: number;
  size: number;
}

/** Kosik nema vlastni ID - identifikuje ho uzivatel, organizace a portal. */
export interface CartKey {
  userId: number;
  organizationId: number;
  portalId: number;
}

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

interface AdminCartStoreState {
  loading: boolean;
  error: string | null;
  data: AdminCartListResponse[] | null;
  pagination: PaginationInfo | null;
  detail: AdminCartDetailResponse | null;
  detailLoading: boolean;
  detailError: string | null;
  loadCarts: (filters: CartFilters) => Promise<void>;
  loadDetail: (key: CartKey) => Promise<void>;
  clearDetail: () => void;
}

const extractApiError = (error: unknown): string | null => {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || (error instanceof Error ? error.message : null);
};

export const useAdminCartStore = create<AdminCartStoreState>(set => ({
  loading: false,
  error: null,
  data: null,
  pagination: null,
  detail: null,
  detailLoading: false,
  detailError: null,

  loadCarts: async (filters: CartFilters) => {
    set({ loading: true, error: null });
    try {
      const response = await adminCartApi.getAdminCarts({
        page: filters.page,
        size: filters.size,
        q: filters.q,
        organizationId: filters.organizationId,
        inactiveDays: filters.inactiveDays,
      });
      const result = response.data;
      set({
        loading: false,
        data: result.items || [],
        pagination: { page: result.page, size: result.size, total: result.total, totalPages: result.totalPages },
      });
    } catch (error) {
      set({ loading: false, error: extractApiError(error) ?? 'Chyba při načítání košíků' });
    }
  },

  loadDetail: async (key: CartKey) => {
    set({ detailLoading: true, detail: null, detailError: null });
    try {
      const response = await adminCartApi.getAdminCartDetail(key);
      set({ detailLoading: false, detail: response.data });
    } catch (error) {
      // 404 = zakaznik kosik mezitim odeslal nebo vyprazdnil.
      set({ detailLoading: false, detailError: extractApiError(error) ?? 'Chyba při načítání košíku' });
    }
  },

  clearDetail: () => set({ detail: null, detailError: null }),
}));
